// routes/AiNodesPane.jsx — 사람 Node와 Scout한 AI Node의 실제 Bridge 준비 사실.
//
// 이 화면은 AI가 "온라인"인지, 답할 것인지, 자동으로 일을 시작했는지를 말하지 않는다.
// Product Adapter가 실제로 보장하는 것은 키가 발급됐는지와 설치본이 한 번 등록됐는지뿐이다.
// 그래서 표현도 `설치 등록`, `마지막 확인`까지로 제한한다.

import { useRef, useState } from 'preact/hooks';
import { issueBridgeKey, read, revokeBridgeKey, scoutAiNode } from '../api/client.js';
import { invalidate, useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import {
  Button,
  Chip,
  Dialog,
  Field,
  Form,
  IconChip,
  Input,
  List,
  ListRow,
  Notice,
  PageHead,
  Textarea,
} from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';

export function AiNodesPane({ spaceId, members = [] }) {
  const session = useSession();
  const [keyTarget, setKeyTarget] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [scoutOpen, setScoutOpen] = useState(false);
  const readiness = useResource('session:bridge-readiness', () =>
    read('bridge-readiness', { nodeId: session.nodeId }),
  );
  const memberships = new Map(members.map((member) => [member.nodeId, member]));

  return (
    <section class="work">
      <PageHead
        eyebrow="SPACE / 연결"
        title="로컬 AI 연결"
        meta="내 컴퓨터의 AI가 이 Space에서 일할 수 있도록 연결합니다."
      />

      <div class="work__body node-connections">
        <Resource resource={readiness} loadingLabel="AI Node와 Bridge 준비 사실을 읽는 중">
          {(data) => {
            const list = data.nodes ?? [];
            const myNode = list.find((node) => node.nodeId === session.nodeId);
            const scoutedNodes = list.filter((node) => node.nodeId !== session.nodeId);
            if (!myNode) {
              return (
                <Empty title="내 Node의 연결 상태를 아직 읽을 수 없습니다.">
                  현재 웹 세션의 사람 Node를 다시 확인한 뒤, 이 Node에 MCP 연결 키를 발급합니다.
                </Empty>
              );
            }
            return (
              <div class="node-connections__layout">
                <section class="node-journey" aria-labelledby="my-node-heading">
                  <header class="node-journey__head">
                    <div class="node-journey__node">
                      <IconChip object="node" name="node" />
                      <div>
                        <p class="t-meta">연결할 작업 주체</p>
                        <h2 id="my-node-heading">내 Node</h2>
                        <p class="t-meta">{membershipLine(memberships.get(myNode.nodeId))}</p>
                      </div>
                    </div>
                    <Chip tone={bridgeReadinessText(myNode).tone}>{bridgeReadinessText(myNode).label}</Chip>
                  </header>

                  <ol class="node-journey__steps" aria-label="로컬 AI 연결 단계">
                    <li class="node-journey__step node-journey__step--current">
                      <span class="node-journey__number">1</span>
                      <div class="node-journey__copy">
                        <p class="t-meta">시작</p>
                        <h3>연결 키 발급</h3>
                        <p>{bridgeReadinessText(myNode).label === '준비 전'
                          ? '이 컴퓨터의 Bridge에 한 번만 전달할 키를 만듭니다.'
                          : bridgeReadinessText(myNode).description}</p>
                      </div>
                      <div class="node-journey__action">
                        <Button tone="human" size="sm" onClick={() => setKeyTarget(myNode)} disabled={myNode.state !== 'active'}>
                          {bridgeReadinessText(myNode).label === '설치 등록됨' ? '새 연결 키' : '연결 키 발급'}
                        </Button>
                        {(myNode.keys ?? []).length > 0 ? <Button tone="ghost" size="sm" onClick={() => setManageTarget(myNode)}>키 관리</Button> : null}
                      </div>
                    </li>
                    <li class="node-journey__step">
                      <span class="node-journey__number">2</span>
                      <div class="node-journey__copy">
                        <p class="t-meta">완료 확인</p>
                        <h3>Bridge 연결 확인</h3>
                        <p>연결이 끝나면 설치 등록과 마지막 확인 시각이 여기에 표시됩니다.</p>
                      </div>
                    </li>
                  </ol>
                  <p class="node-journey__note t-meta">연결한 로컬 AI의 행동은 새 참여자를 만들지 않고 이 Node에 기록됩니다.</p>
                </section>

                {scoutedNodes.length > 0 ? (
                  <section class="node-directory" aria-labelledby="ai-node-heading">
                    <header class="node-directory__head">
                      <div>
                        <p class="t-meta">별도 역할</p>
                        <h2 id="ai-node-heading">독립 AI Node</h2>
                      </div>
                      <Button tone="ghost" size="sm" onClick={() => setScoutOpen(true)}>추가</Button>
                    </header>
                    <List class="node-connection-list">
                      {scoutedNodes.map((node) => (
                        <NodeRow
                          key={node.nodeId}
                          node={node}
                          membership={memberships.get(node.nodeId)}
                          onIssueKey={() => setKeyTarget(node)}
                          onManageKeys={() => setManageTarget(node)}
                        />
                      ))}
                    </List>
                  </section>
                ) : (
                  <button class="node-add-secondary" type="button" onClick={() => setScoutOpen(true)}>
                    <span class="node-add-secondary__plus" aria-hidden="true">+</span>
                    <span><strong>독립 AI Node 추가</strong><small>다른 역할과 참여 경계가 필요할 때만 만듭니다.</small></span>
                  </button>
                )}
              </div>
            );
          }}
        </Resource>

        {scoutOpen ? (
          <Dialog title="AI Node 데려오기" onClose={() => setScoutOpen(false)}>
            <ScoutAiNode
              actorNodeId={session.nodeId}
              onClose={() => setScoutOpen(false)}
              onScouted={(node) => {
                setScoutOpen(false);
                setKeyTarget(node);
                invalidate('session:bridge-readiness');
              }}
            />
          </Dialog>
        ) : null}

        {keyTarget ? (
          <Dialog title={`${keyTarget.nodeId === session.nodeId ? '내 Node MCP 연결' : keyTarget.displayName ?? 'AI Node'} Bridge 키`} onClose={() => setKeyTarget(null)}>
            <BridgeKeyIssue
              actorNodeId={session.nodeId}
              node={keyTarget}
              ownNode={keyTarget.nodeId === session.nodeId}
              onClose={() => setKeyTarget(null)}
              onIssued={() => {
                invalidate('session:bridge-readiness');
              }}
            />
          </Dialog>
        ) : null}
        {manageTarget ? (
          <Dialog title={`${manageTarget.nodeId === session.nodeId ? '내 Node MCP 연결' : manageTarget.displayName ?? 'AI Node'} 키 관리`} onClose={() => setManageTarget(null)}>
            <BridgeKeyManage
              actorNodeId={session.nodeId}
              node={manageTarget}
              onClose={() => setManageTarget(null)}
              onChanged={() => invalidate('session:bridge-readiness')}
            />
          </Dialog>
        ) : null}
      </div>
    </section>
  );
}

function ScoutAiNode({ actorNodeId, onClose, onScouted }) {
  // 두 초안은 제출 때만 읽는다. 한글 IME 조합 중 상태 갱신으로 입력 DOM을 교체하지 않는다.
  const displayNameRef = useRef(null);
  const purposeRef = useRef(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const displayName = displayNameRef.current?.value.trim() ?? '';
    const purpose = purposeRef.current?.value.trim() ?? '';
    if (displayName === '' || purpose === '') {
      setError('AI Node 이름과 맡길 일을 모두 적어 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await scoutAiNode({ displayName, purpose }, { actorNodeId });
      onScouted(result.node);
    } catch (cause) {
      setError(cause?.message || 'AI Node를 데려오지 못했습니다. 다시 확인해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Form onSubmit={submit} busy={busy} class="stack">
        <Notice tone="neutral" title="사람이 AI를 Scout합니다.">
          이 사람 Node가 AI를 데려왔다는 관계와 맡길 일을 기록합니다. 이 단계는 아직 Space 참여나
          자동 실행을 허용하지 않습니다.
        </Notice>
        <Field label="AI Node 이름" htmlFor="scout-ai-name" required>
          <Input
            ref={displayNameRef}
            id="scout-ai-name"
            placeholder="예: AXIM Scout"
            autocomplete="off"
            disabled={busy}
          />
        </Field>
        <Field
          label="AI에게 맡길 일"
          htmlFor="scout-ai-purpose"
          hint="이 설명은 역할의 출발점입니다. 실제 권한은 Space와 Bridge가 따로 정합니다."
          required
          error={error}
        >
          <Textarea
            ref={purposeRef}
            id="scout-ai-purpose"
            rows={4}
            placeholder="예: 자료를 읽고 근거를 모아 Thread에 제안합니다. 사람의 직접 수락은 하지 않습니다."
            disabled={busy}
          />
        </Field>
        <div class="row">
          <Button tone="human" kind="submit" busy={busy}>AI Node 만들기</Button>
          <Button onClick={onClose} disabled={busy}>취소</Button>
        </div>
    </Form>
  );
}

function NodeRow({ node, membership, ownNode = false, onIssueKey, onManageKeys }) {
  const membershipText = membershipLine(membership);
  const bridge = bridgeReadinessText(node);

  return (
    <ListRow class="node-connection-row">
      <IconChip object="node" name="node" />
      <span class="grow">
        <span class="t-body-strong">{ownNode ? '내 Node' : node.displayName ?? '이름 없는 AI Node'}</span>
        <span class="t-meta">{ownNode ? membershipText : `별도 AI Node · ${membershipText}`}</span>
        <span class="t-meta node-connection-row__detail">{bridge.description}</span>
      </span>
      <span class="node-connection-row__status">
        <Chip tone={bridge.tone}>{bridge.label}</Chip>
        <span class="t-meta">{nodeStateText(node.state)}</span>
      </span>
      <Button tone="ghost" size="sm" onClick={onIssueKey} disabled={node.state !== 'active'}>
        {bridge.label === '설치 등록됨'
          ? '새 키'
          : ownNode ? '연결 키 발급' : 'Bridge 키 발급'}
      </Button>
      {(node.keys ?? []).length > 0 ? <Button tone="ghost" size="sm" onClick={onManageKeys}>관리</Button> : null}
    </ListRow>
  );
}

function membershipLine(membership) {
  if (!membership) return '이 Space에 아직 참여하지 않음';
  if (membership.state && membership.state !== 'active') {
    return membership.state === 'revoked' ? '참여 해제됨' : '참여 상태를 확인해야 함';
  }
  return `${roleLabel(membership.role)}로 참여 중`;
}

function BridgeKeyIssue({ actorNodeId, node, ownNode = false, onClose, onIssued }) {
  // 설치 라벨은 제출 직전에만 읽는 로컬 초안이다. 한글 IME 조합 중 state 갱신으로
  // 입력 노드가 다시 만들어지면 포커스가 한 글자마다 빠질 수 있다.
  const labelRef = useRef(null);
  const [apiKey, setApiKey] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function submit() {
    const trimmedLabel = labelRef.current?.value.trim() ?? '';
    if (trimmedLabel.length === 0) {
      setError('이 키를 구분할 컴퓨터 또는 설치 위치 이름을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await issueBridgeKey({
        nodeId: node.nodeId,
        actorNodeId,
        label: trimmedLabel,
      });
      setApiKey(result.apiKey);
      onIssued();
    } catch (cause) {
      setError(cause?.message || '연결 키를 발급하지 못했습니다. 다시 확인해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  async function copyKey() {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
    } catch {
      setError('복사하지 못했습니다. 키를 직접 선택해 복사한 뒤 닫아 주세요.');
    }
  }

  return (
    <div class="stack">
      {apiKey ? (
        <div class="stack">
          <Notice tone="waiting" title="이 값은 지금 한 번만 볼 수 있습니다.">
            이 화면을 닫거나 새로고침하면 같은 키를 다시 표시할 수 없습니다. 로컬 Bridge의
            대화형 로그인 단계에 붙여 넣은 뒤, 확인하고 닫아 주세요.
          </Notice>
          <Field label="한 번 전달할 연결 키" htmlFor="bridge-issued-key">
            <Textarea id="bridge-issued-key" value={apiKey} readonly rows={3} />
          </Field>
          <div class="bridge-steps" aria-label="Bridge와 MCP 연결 순서">
            <p class="t-body-strong">로컬 AI가 있는 컴퓨터에서 이어서 합니다.</p>
            <p>
              <a
                class="btn btn--neutral btn--sm"
                href="/releases/loom-bridge/0.1.0/install-loom-bridge-0.1.0-macos-arm64.sh"
              >
                <span class="btn__label">macOS Apple Silicon 설치 파일 받기</span>
              </a>
            </p>
            <p class="t-meta">
              현재 설치 파일은 Apple Silicon Mac용입니다. Node.js 20.11.1 이상이 필요하며, 설치 키를
              명령줄이나 설정 파일에는 절대 넣지 않습니다.
            </p>
            <ol>
              <li>받은 파일을 실행해 <code>loom-bridge</code> CLI를 설치합니다.</li>
              <li><code>loom-bridge login</code>을 실행하고, 위 키는 숨겨진 프롬프트에만 붙여 넣습니다.</li>
              <li><code>loom-bridge configure codex</code>로 Codex MCP 연결을 만듭니다.</li>
              <li>
                <code>loom-bridge status</code>로 설치 등록을 확인합니다. {ownNode
                  ? '이후 로컬 AI의 행동은 이미 참여한 내 Node에서 delegated_ai로 기록됩니다. 별도 참여 요청은 하지 않습니다.'
                  : '이후 AI Node의 Space 참여를 요청합니다.'}
              </li>
            </ol>
            <p class="t-meta">MCP 설정 파일에는 연결 키를 넣지 않습니다. 키는 Bridge의 로컬 자격 저장소에만 남습니다.</p>
          </div>
          <div class="row">
            <Button tone="human" onClick={copyKey}>{copied ? '복사됨' : '키 복사'}</Button>
            <Button onClick={onClose}>확인했고 닫기</Button>
          </div>
        </div>
      ) : (
        <Form onSubmit={submit} busy={busy} class="stack">
          <Notice tone="neutral" title={ownNode ? '내 Node에 MCP를 연결합니다.' : '키 원문은 Loom에 다시 저장하지 않습니다.'}>
            {ownNode
              ? '이 키는 현재 Space에 이미 참여한 사람 Node를 로컬 Bridge에 연결합니다. 로컬 AI의 행동은 delegated_ai로 남으며, 새 사람이나 AI Node를 만들지 않습니다.'
              : '키는 발급 응답에서 한 번만 전달됩니다. 설치 중 문제가 생기면 새 키를 발급하고, 이전 키는 Bridge CLI에서 폐기할 수 있습니다.'}
          </Notice>
          <Field
            label="이 키를 쓸 설치 위치"
            htmlFor="bridge-key-label"
            hint="예: 영호의 MacBook, 빌드 서버의 Scout"
            required
            error={error}
          >
            <Input
              ref={labelRef}
              id="bridge-key-label"
              placeholder="설치 위치 이름"
              autocomplete="off"
              disabled={busy}
            />
          </Field>
          <div class="row">
            <Button tone="human" kind="submit" busy={busy}>한 번 전달할 키 발급</Button>
            <Button onClick={onClose} disabled={busy}>취소</Button>
          </div>
        </Form>
      )}
    </div>
  );
}

function BridgeKeyManage({ actorNodeId, node, onClose, onChanged }) {
  const [confirmKeyId, setConfirmKeyId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function revoke(keyId) {
    setBusy(true);
    setError(null);
    try {
      await revokeBridgeKey({ actorNodeId, keyId });
      setConfirmKeyId(null);
      onChanged();
    } catch (cause) {
      setError(cause?.message || '키를 폐기하지 못했습니다. 다시 확인해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  const keys = node.keys ?? [];
  return (
    <div class="stack">
        <Notice tone="neutral" title="폐기하면 이 키가 만든 설치본과 세션도 함께 끊깁니다.">
          이 화면은 키 원문을 보관하거나 다시 보여 주지 않습니다. 새 컴퓨터를 연결하려면 별도로
          새 키를 발급하세요.
        </Notice>
        {error ? <Notice tone="danger" title="키를 폐기하지 못했습니다.">{error}</Notice> : null}
        <List>
          {keys.map((key) => (
            <ListRow key={key.keyId}>
              <IconChip object="node" name="node" />
              <span class="grow">
                <span class="t-body-strong">{key.label}</span>
                <span class="t-meta">발급 {formatTime(key.createdAt)} · 만료 {formatTime(key.expiresAt)}</span>
                <span class="t-meta">마지막 사용 {key.lastUsedAt ? formatTime(key.lastUsedAt) : '기록 없음'}</span>
              </span>
              <Chip tone={key.status === 'active' ? 'waiting' : 'neutral'}>{keyStatusLabel(key.status)}</Chip>
              {key.status === 'active' ? (
                confirmKeyId === key.keyId ? (
                  <span class="row">
                    <Button tone="danger" size="sm" busy={busy} onClick={() => revoke(key.keyId)}>정말 폐기</Button>
                    <Button size="sm" disabled={busy} onClick={() => setConfirmKeyId(null)}>취소</Button>
                  </span>
                ) : (
                  <Button tone="danger" size="sm" disabled={busy} onClick={() => setConfirmKeyId(key.keyId)}>폐기</Button>
                )
              ) : null}
            </ListRow>
          ))}
        </List>
        <div class="row">
          <Button onClick={onClose}>관리 닫기</Button>
        </div>
    </div>
  );
}

function bridgeReadinessText(node) {
  const installations = (node.installations ?? []).filter((item) => item.status === 'active');
  const keys = (node.keys ?? []).filter((item) => item.status === 'active');
  const latest = installations
    .map((item) => item.lastSeenAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  if (installations.length > 0) {
    return {
      tone: 'accepted',
      label: '설치 등록됨',
      description: latest ? `마지막 확인 ${formatTime(latest)}` : '설치 등록은 되었으나 아직 확인 시각이 없습니다.',
    };
  }
  if (keys.length > 0) {
    return {
      tone: 'waiting',
      label: '설치 등록 대기',
      description: '연결 키가 발급되었습니다. 로컬 Bridge 설치를 마치면 등록 사실이 나타납니다.',
    };
  }
  return {
    tone: 'neutral',
    label: '준비 전',
    description: '발급된 연결 키나 설치 등록이 아직 없습니다.',
  };
}

function formatTime(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '알 수 없음' : date.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

function nodeStateText(state) {
  if (state === 'active') return 'Node 활성';
  if (state === 'suspended') return 'Node 정지됨';
  if (state === 'retired') return 'Node 은퇴됨';
  return 'Node 상태를 읽지 못함';
}

function keyStatusLabel(status) {
  if (status === 'active') return '사용 가능';
  if (status === 'revoked') return '폐기됨';
  if (status === 'expired') return '만료됨';
  return '상태 미확인';
}

function roleLabel(role) {
  if (role === 'owner') return '오너';
  if (role === 'steward') return '관리자';
  return '참여자';
}
